import React, { useRef, useState } from 'react';
import { Input, Spin } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { Category } from './types';
import {
  CheckIconWrap,
  DropdownEmptyMsg,
  DropdownLoadingWrap,
  SearchWrapper,
  SelectedBoxEmpty,
  SelectedBoxLabel,
  SelectedItemText,
  SelectedTagClose,
  SelectedTagItem,
  SelectedTagsBox,
  SuggestionItem,
  SuggestionsDropdown,
  TagsWrap,
} from './styles';

interface CategoriesSectionProps {
  allCategories: Category[];
  selected: Category[];
  onChange: (selected: Category[]) => void;
  isDarkMode: boolean;
  isLoading?: boolean;
}

const CategoriesSection: React.FC<CategoriesSectionProps> = ({
  allCategories,
  selected,
  onChange,
  isDarkMode,
  isLoading,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState(false);

  const filtered = query.trim()
    ? allCategories.filter((c) =>
        c.category.toLowerCase().includes(query.toLowerCase())
      )
    : allCategories;

  const handleToggle = (cat: Category) => {
    if (selected.some((s) => s.id === cat.id)) {
      onChange(selected.filter((s) => s.id !== cat.id));
    } else {
      onChange([...selected, { ...cat, order: selected.length }]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === allCategories.length) {
      onChange([]);
    } else {
      onChange(allCategories.map((c, i) => ({ ...c, order: i })));
    }
  };

  const handleRemove = (id: number | string) => {
    onChange(selected.filter((s) => s.id !== id));
  };

  return (
    <>
      <SearchWrapper ref={wrapperRef}>
        <Input
          placeholder="Search categories..."
          value={query}
          allowClear
          suffix={isLoading ? <Spin size="small" /> : undefined}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setShow(true)}
          onBlur={() => setTimeout(() => setShow(false), 150)}
        />
        {show && (
          <SuggestionsDropdown $isDark={isDarkMode}>
            {isLoading ? (
              <DropdownLoadingWrap>
                <Spin size="small" />
              </DropdownLoadingWrap>
            ) : filtered.length === 0 ? (
              <DropdownEmptyMsg>
                {query.trim() ? 'No results found.' : 'No categories available.'}
              </DropdownEmptyMsg>
            ) : (
              <>
                <SuggestionItem
                  $isDark={isDarkMode}
                  $isSelectAll
                  onMouseDown={(e) => { e.preventDefault(); handleSelectAll(); }}
                >
                  {selected.length === allCategories.length ? 'Unselect All' : 'Select All'}
                </SuggestionItem>
                {filtered.map((cat) => {
                  const isChecked = selected.some((s) => s.id === cat.id);
                  return (
                    <SuggestionItem
                      key={cat.id}
                      $isDark={isDarkMode}
                      onMouseDown={(e) => { e.preventDefault(); handleToggle(cat); }}
                    >
                      {isChecked
                        ? <SelectedItemText>{cat.category}</SelectedItemText>
                        : <span>{cat.category}</span>}
                      {isChecked && (
                        <CheckIconWrap><CheckOutlined /></CheckIconWrap>
                      )}
                    </SuggestionItem>
                  );
                })}
              </>
            )}
          </SuggestionsDropdown>
        )}
      </SearchWrapper>

      <SelectedTagsBox $isDark={isDarkMode}>
        <SelectedBoxLabel $isDark={isDarkMode}>Selected Categories:</SelectedBoxLabel>
        {selected.length === 0 ? (
          <SelectedBoxEmpty $isDark={isDarkMode}>No categories selected.</SelectedBoxEmpty>
        ) : (
          <TagsWrap>
            {selected.map((cat) => (
              <SelectedTagItem key={cat.id}>
                {cat.category}
                <SelectedTagClose onClick={() => handleRemove(cat.id)}>✕</SelectedTagClose>
              </SelectedTagItem>
            ))}
          </TagsWrap>
        )}
      </SelectedTagsBox>
    </>
  );
};

export default CategoriesSection;
